import { evaluateAuthority, type AuthorityRule } from "../lib/governance/authority";
import {
  requestFromRow,
  signoffFromRow,
  signoffEligibility,
  requiredRoles,
  requestStatus,
  PROPOSER_ROLES,
} from "../lib/warranty/pfmea-signoff";
import { validatePfmeaChange } from "../lib/warranty/pfmea-governance";
import {
  canChangeVerdict,
  validateVerdictChange,
  verdictRuleKey,
} from "../lib/warranty/compliance-verdict";
import { validateCarryFactor } from "../lib/warranty/carry-factor";
import { stageGate, dGate, normalizeMapping } from "../lib/warranty/stage-link";
import type { ProblemRecord, PfmeaAfterField } from "../lib/warranty/types";
import {
  clone,
  fail,
  hasRole,
  now,
  persona,
  requireRole,
  table,
  uid,
  type DemoSession,
  type Row,
} from "./state";

const EDITORS = ["admin", "quality_director", "quality_engineer"];
const CONTRIBUTORS = [...EDITORS, "manufacturing_engineer", "supplier_quality"];
const DIRECTORS = ["admin", "quality_director"];
const eq = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const closureFingerprint = (record: ProblemRecord) => JSON.stringify({ ...record, audit: [] });
function rationale(value: unknown, minimum = 10): string {
  const text = String(value ?? "").trim();
  if (text.length < minimum)
    fail(`A rationale or comment of at least ${minimum} characters is required.`);
  return text;
}
function findRecord(s: DemoSession, id: unknown) {
  return (
    table(s, "problem_records").find((r) => r.id === id) ??
    fail("Problem Record not found in this demo session.")
  );
}
function policy(s: DemoSession, key: string, fallback: unknown) {
  return table(s, "policy_settings").find((r) => r.key === key)?.value ?? fallback;
}
function append(s: DemoSession, name: string, row: Row): Row {
  const result = {
    id: uid(),
    created_at: now(),
    ...row,
    simulation: true,
    classification: "SYNTHETIC",
  };
  table(s, name).push(result);
  return result;
}
function audit(
  s: DemoSession,
  recordId: string,
  eventType: string,
  action: string,
  detail: string,
  payload: Row = {},
  rule = eventType,
) {
  const actor = persona(s);
  const record = table(s, "problem_records").find((r) => r.id === recordId);
  const row = append(s, "audit_events", {
    entity_type: "problem_record",
    entity_id: recordId,
    record_code: record?.code ?? "",
    stage_id: "",
    event_type: eventType,
    action: `${action} (simulated)`,
    detail,
    actor_user_id: actor.id,
    actor_label: actor.name,
    authority_rule_key: rule,
    payload: { ...payload, simulation: true },
    occurred_at: now(),
  });
  if (record?.record) {
    record.record.audit ??= [];
    record.record.audit.push({
      id: row.id,
      at: now(),
      actor: actor.name,
      action: row.action,
      clause: "Demo governance",
      detail,
    });
    record.updated_at = now();
  }
  return row.id;
}
function snapshot(s: DemoSession, recordId: string): Row {
  const r = findRecord(s, recordId).record as ProblemRecord;
  return {
    simulation: true,
    capturedAt: now(),
    risk: r.risk,
    riskScore: r.riskScore,
    status: r.status,
    exposure: r.financial.exposure,
    actualCost: r.financial.actualCost,
    stagesComplete: r.stages.filter((x) => x.status === "complete").length,
    evidenceCount: r.stages.reduce((n, x) => n + x.evidence.length, 0),
    violatedClauses: r.clauses.filter((x) => x.verdict === "Violated").length,
    evidence: r.stages.flatMap((x) =>
      x.evidence.map((e) => ({ name: e.name, stage: x.name, addedAt: e.addedAt })),
    ),
  };
}
function authority(
  s: DemoSession,
  decisionType: string,
  record: Pick<ProblemRecord, "riskScore" | "financial">,
) {
  const verdict = evaluateAuthority({
    decisionType,
    record,
    roles: persona(s).roles,
    rules: table(s, "decision_authority_rules").filter(
      (r) => r.active !== false,
    ) as unknown as AuthorityRule[],
  });
  if (!verdict.authorised) fail(`Simulated authority refused: ${verdict.reason}`);
  return verdict;
}

/** Validate the exact source-owned table writes; governed functions have no raw-write bypass. */
export function validateWrite(
  s: DemoSession,
  name: string,
  operation: string,
  row: Row,
  previous?: Row,
): Row {
  requireRole(s, CONTRIBUTORS);
  if (
    [
      "pfmea_change_requests",
      "pfmea_signoffs",
      "compliance_verdict_changes",
      "financial_carry_factor_changes",
      "escalations",
      "escalation_events",
      "escalation_trigger_thresholds",
      "escalation_threshold_changes",
      "user_roles",
    ].includes(name)
  )
    fail(`${name} changes require a supported demo command.`);
  if (
    ![
      "problem_records",
      "record_problem_statements",
      "audit_events",
      "evidence_items",
      "evidence_snapshots",
      "signals",
      "signal_root_cause_evidence",
      "signal_dispositions",
      "signal_severity_assessments",
      "tasks",
      "notifications",
      "ai_interactions",
      "data_lineage",
      "policy_settings",
      "integration_configs",
    ].includes(name)
  )
    fail(`Unsupported demo table mutation: ${name}`);
  if (
    [
      "audit_events",
      "evidence_snapshots",
      "signal_dispositions",
      "signal_severity_assessments",
    ].includes(name) &&
    operation !== "insert"
  )
    fail(`${name} is append-only in the demo.`);
  if (
    [
      "profiles",
      "decision_authority_rules",
      "source_systems",
      "integration_configs",
      "policy_settings",
    ].includes(name)
  )
    requireRole(s, DIRECTORS);
  const actor = persona(s);
  if ("tenant_id" in row && row.tenant_id !== s.stored.tenantId)
    fail("A demo query cannot write another tenant's data.");
  if ("membership_id" in row && row.membership_id !== s.stored.membershipId)
    fail("A demo query cannot write another membership's data.");
  if (name === "audit_events") {
    if (row.event_type !== "recommendation.decided") delete s.pendingWrites[row.entity_id];
    return {
      ...row,
      actor_user_id: actor.id,
      actor_label: actor.name,
      action: `${String(row.action ?? "Demo action").replace(/ \(simulated\)$/, "")} (simulated)`,
      occurred_at: now(),
      payload: { ...row.payload, simulation: true },
    };
  }
  if (name === "evidence_snapshots") {
    delete s.pendingWrites[row.record_id];
    return {
      ...row,
      decided_by_user_id: actor.id,
      decided_by_label: actor.name,
      taken_at: now(),
      snapshot: { ...row.snapshot, simulation: true },
    };
  }
  if (name === "evidence_items") return { ...row, owner_user_id: actor.id, owner: actor.name };
  if (
    name === "signals" ||
    name === "signal_dispositions" ||
    name === "signal_severity_assessments"
  ) {
    requireRole(s, EDITORS);
    const signal =
      name === "signals"
        ? (previous ?? row)
        : (table(s, "signals").find((r) => r.id === row.signal_id) ?? fail("Signal not found."));
    if (name === "signal_dispositions") {
      const decisionType = row.disposition === "create" ? "signal.create" : "signal.disposition";
      authority(s, decisionType, {
        riskScore: Number(signal.current_severity ?? 1) * 20 - 20,
        financial: { exposure: Number(signal.financial_exposure) } as never,
      });
      rationale(row.rationale, 1);
      if (!["create", "link", "monitor", "nfa"].includes(row.disposition))
        fail("Unsupported signal disposition.");
      if (["create", "link"].includes(row.disposition)) findRecord(s, row.target_record_id);
      if (
        !table(s, "audit_events").some(
          (a) => a.id === row.audit_event_id && a.actor_user_id === actor.id,
        )
      )
        fail("A confirmed local audit entry is required.");
      return {
        ...row,
        decided_by_user_id: actor.id,
        decided_by_label: actor.name,
        evidence_snapshot: { ...row.evidence_snapshot, simulation: true },
      };
    }
    if (name === "signal_severity_assessments") {
      if (!Number.isInteger(row.severity) || row.severity < 1 || row.severity > 5)
        fail("Signal severity must be 1–5.");
      const verdict = authority(s, "signal.severity", {
        riskScore: row.severity * 20,
        financial: { exposure: Number(signal.financial_exposure) } as never,
      });
      rationale(row.rationale, 1);
      const independent = table(s, name).some(
        (a) =>
          a.signal_id === row.signal_id &&
          a.severity === row.severity &&
          a.assessed_by_user_id !== actor.id,
      );
      return {
        ...row,
        assessed_by_user_id: actor.id,
        assessed_by_label: actor.name,
        second_approval_required:
          (verdict.requiresSecondApproval || row.severity === 5) && !independent,
      };
    }
    if (
      previous &&
      row.current_severity !== undefined &&
      row.current_severity !== previous.current_severity
    ) {
      const assessment = [...table(s, "signal_severity_assessments")]
        .reverse()
        .find(
          (a) =>
            a.signal_id === previous.id &&
            a.severity === row.current_severity &&
            a.assessed_by_user_id === actor.id &&
            !a.second_approval_required,
        );
      if (!assessment) fail("A confirmed authorized severity assessment is required.");
    }
  }
  if (name === "problem_records") {
    const next = clone(row.record ?? previous?.record) as ProblemRecord;
    if (!next || next.id !== row.id) fail("A matching Problem Record body is required.");
    if (operation === "delete") {
      requireRole(s, EDITORS);
      return row;
    }
    if (!previous) {
      requireRole(s, EDITORS);
      return { ...row, record: next };
    }
    const before = previous.record as ProblemRecord;
    // The copied store compensates a failed governance write with exactly its
    // preceding record body. This receipt permits that one compensation only.
    const pending = s.pendingWrites[row.id];
    if (
      pending &&
      pending.actorId === actor.id &&
      eq(next, pending.before.record) &&
      eq(before, pending.after)
    ) {
      delete s.pendingWrites[row.id];
      return clone(pending.before);
    }
    if (!hasRole(s, EDITORS)) {
      const a = clone(before) as unknown as Row;
      const b = clone(next) as unknown as Row;
      for (const key of ["stages", "eightD"]) {
        a[key] = a[key].map((stage: Row) => ({ ...stage, evidence: [], status: "comparison" }));
        b[key] = b[key].map((stage: Row) => ({ ...stage, evidence: [], status: "comparison" }));
      }
      a.audit = [];
      b.audit = [];
      if (!eq(a, b))
        fail("This simulated persona may attach evidence but cannot edit the Problem Record.");
    }
    if (
      !eq(next.pfmea, before.pfmea) ||
      !eq(next.clauses, before.clauses) ||
      next.financial.monthlyCarryFactor !== before.financial.monthlyCarryFactor
    )
      fail("PFMEA, compliance verdict and carry-factor changes require governed demo commands.");
    if (
      next.audit.length < before.audit.length ||
      !eq(next.audit.slice(0, before.audit.length), before.audit)
    )
      fail("Existing local audit history must be preserved.");
    const mapping = normalizeMapping(policy(s, "workflow.eightd_mapping", null));
    for (const stage of next.stages) {
      const old = before.stages.find((x) => x.id === stage.id) ?? fail("Unknown workflow stage.");
      if (old.evidence.some((e) => !stage.evidence.some((x) => eq(e, x))))
        fail("Existing stage evidence must be preserved.");
      if (stage.status === "complete" && old.status !== "complete") {
        requireRole(s, EDITORS);
        if (before.stages.some((x) => x.index < stage.index && x.status !== "complete"))
          fail("Complete earlier workflow stages first.");
        const gate = stageGate(next, stage, mapping);
        if (gate.blocked) fail(gate.message ?? "Stage evidence is incomplete.");
      }
    }
    for (const d of next.eightD) {
      const old = before.eightD.find((x) => x.id === d.id) ?? fail("Unknown 8D step.");
      if (old.evidence.some((e) => !d.evidence.some((x) => eq(e, x))))
        fail("Existing 8D evidence must be preserved.");
      if (d.status === "complete" && old.status !== "complete") {
        requireRole(s, EDITORS);
        const gate = dGate(next, d, mapping);
        if (gate.blocked) fail(gate.message ?? "8D evidence is incomplete.");
      }
    }
    if (!eq(next.decisions, before.decisions)) {
      requireRole(s, EDITORS);
      authority(s, "recommendation", next);
    }
    // The exported store leaves the final record status unchanged; the local contract closes it.
    if (next.stages.length && next.stages.every((x) => x.status === "complete")) {
      if (
        table(s, "pfmea_change_requests").some(
          (req) =>
            req.record_id === next.id &&
            requestStatus(
              table(s, "pfmea_signoffs")
                .filter((x) => x.request_id === req.id)
                .map((x) => signoffFromRow(x as never)),
            ) === "pending",
        )
      )
        fail("Resolve pending PFMEA change requests before final closure.");
      if (before.status !== "closed") {
        const verdict = authority(s, "record_closure", next);
        if (verdict.requiresSecondApproval) {
          const request = [...table(s, "audit_events")]
            .reverse()
            .find(
              (event) =>
                event.entity_id === next.id && event.event_type === "demo.closure_requested",
            );
          if (!request || request.payload?.fingerprint !== closureFingerprint(before))
            fail(
              "Request closure review after the final evidence gate is ready. Changes require a fresh request.",
            );
          if (request.actor_user_id === actor.id)
            fail("A distinct authorized simulated reviewer must close this record.");
        }
      }
      next.status = "closed";
    } else if (next.status === "closed" && before.status !== "closed")
      fail("Complete every required workflow stage before closure.");
    if (next.audit.length > before.audit.length)
      s.pendingWrites[row.id] = { before: clone(previous), after: clone(next), actorId: actor.id };
    return { ...row, record: next, status: next.status, updated_at: now() };
  }
  return row;
}

function pfmea(s: DemoSession, fn: string, a: Row): unknown {
  const actor = persona(s);
  if (fn === "pfmea_propose_change") {
    requireRole(s, PROPOSER_ROLES);
    const record = findRecord(s, a.p_record_id);
    const body = record.record as ProblemRecord;
    const row = body.pfmea.find((p) => p.id === a.p_row_id);
    if (!["sevAfter", "occAfter", "detAfter"].includes(a.p_field)) fail("Unknown PFMEA field.");
    const invalid = validatePfmeaChange(row, a.p_field, a.p_new_value, String(a.p_rationale ?? ""));
    if (invalid || !row) fail(invalid ?? "PFMEA row not found.");
    if (
      table(s, "pfmea_change_requests").some(
        (r) =>
          r.record_id === record.id &&
          r.row_id === row.id &&
          r.field === a.p_field &&
          requestStatus(
            table(s, "pfmea_signoffs")
              .filter((x) => x.request_id === r.id)
              .map((x) => signoffFromRow(x as never)),
          ) === "pending",
      )
    )
      fail("A change to this rating is already pending sign-off.");
    const old = row[a.p_field as PfmeaAfterField];
    const next = { ...row, [a.p_field]: a.p_new_value };
    const request = append(s, "pfmea_change_requests", {
      record_id: record.id,
      record_code: record.code,
      row_id: row.id,
      field: a.p_field,
      old_value: old,
      new_value: a.p_new_value,
      rpn_before: row.sevAfter * row.occAfter * row.detAfter,
      rpn_after: next.sevAfter * next.occAfter * next.detAfter,
      rationale: rationale(a.p_rationale),
      proposed_by_user_id: actor.id,
      proposed_by_label: actor.name,
      authority_rule_key: "pfmea.propose",
    });
    request.audit_event_id = audit(
      s,
      record.id,
      "pfmea.change_proposed",
      "PFMEA rating change proposed",
      request.rationale,
      { requestId: request.id },
    );
    return request.id;
  }
  const req =
    table(s, "pfmea_change_requests").find((r) => r.id === a.p_request_id) ??
    fail("PFMEA change request not found.");
  const signs = table(s, "pfmea_signoffs").filter((r) => r.request_id === req.id);
  const eligible = signoffEligibility({
    userId: actor.id,
    roles: actor.roles,
    request: requestFromRow(req as never),
    signoffs: signs.map((r) => signoffFromRow(r as never)),
  });
  if (!eligible.roles.includes(a.p_role))
    fail(eligible.reason ?? "This simulated persona cannot sign under that role.");
  if (!["approve", "reject"].includes(a.p_decision)) fail("Unknown PFMEA sign-off decision.");
  const comment = rationale(a.p_comment, 5);
  const auditId = audit(
    s,
    req.record_id,
    a.p_decision === "reject" ? "pfmea.change_rejected" : "pfmea.change_approved",
    `PFMEA ${a.p_role} ${a.p_decision}`,
    comment,
    { requestId: req.id },
  );
  signs.push(
    append(s, "pfmea_signoffs", {
      request_id: req.id,
      step: a.p_decision === "reject" ? "reject" : `approve_${a.p_role}`,
      role_basis: a.p_role,
      decided_by_user_id: actor.id,
      decided_by_label: actor.name,
      comment,
      audit_event_id: auditId,
      authority_rule_key: `pfmea.approve_${a.p_role}`,
    }),
  );
  if (a.p_decision === "reject") return "rejected";
  if (
    !requiredRoles(requestFromRow(req as never)).every((role) =>
      signs.some((x) => x.step === `approve_${role}`),
    )
  )
    return "approved";
  const record = findRecord(s, req.record_id);
  const row =
    (record.record as ProblemRecord).pfmea.find((r) => r.id === req.row_id) ??
    fail("PFMEA row not found.");
  const field = req.field as PfmeaAfterField;
  if (row[field] !== req.old_value)
    fail("The rating changed since this proposal; nothing was applied.");
  const approvals = signs
    .filter((x) => x.step.startsWith("approve_"))
    .map((x) => ({
      role: x.role_basis,
      by: x.decided_by_label,
      comment: x.comment,
      at: x.created_at,
    }));
  row[field] = req.new_value;
  row.revisions = [
    ...(row.revisions ?? []),
    {
      id: uid(),
      field,
      oldValue: req.old_value,
      newValue: req.new_value,
      rpnBefore: req.rpn_before,
      rpnAfter: req.rpn_after,
      rationale: req.rationale,
      author: req.proposed_by_label,
      at: now(),
      requestId: req.id,
      approvals,
    },
  ];
  const appliedId = audit(
    s,
    req.record_id,
    "pfmea.updated",
    "PFMEA rating revised after all required simulated sign-offs",
    req.rationale,
    { requestId: req.id, approvals },
  );
  append(s, "pfmea_signoffs", {
    request_id: req.id,
    step: "applied",
    role_basis: "",
    decided_by_user_id: actor.id,
    decided_by_label: actor.name,
    comment: "Applied in the local synthetic simulation.",
    audit_event_id: appliedId,
  });
  return "applied";
}

function escalationEvent(
  s: DemoSession,
  esc: Row,
  event: string,
  from: string,
  detail: string,
  payload: Row = {},
) {
  const auditId = audit(s, esc.record_id, `escalation.${event}`, `Escalation ${event}`, detail, {
    ...payload,
    escalationId: esc.id,
    evidenceSnapshot: snapshot(s, esc.record_id),
  });
  append(s, "escalation_events", {
    escalation_id: esc.id,
    record_id: esc.record_id,
    record_code: esc.record_code,
    event_type: event,
    from_status: from,
    to_status: esc.status,
    actor_user_id: persona(s).id,
    actor_label: persona(s).name,
    detail,
    payload: { ...payload, simulation: true },
    audit_event_id: auditId,
    occurred_at: now(),
  });
  esc.updated_at = now();
  return auditId;
}
function escalationMerge(
  s: DemoSession,
  recordId: string,
  level: number,
  category: string,
  triggers: Row[],
  method: string,
  safety: boolean,
  reason: string,
  decisionRequired = "",
) {
  const record = findRecord(s, recordId);
  if (safety) level = 3;
  const due = new Date(
    Date.now() +
      Number(
        (policy(s, "escalation.decision_due_hours", { "2": 48, "3": 24 }) as Row)[String(level)] ??
          48,
      ) *
        3600000,
  ).toISOString();
  let esc = [...table(s, "escalations")]
    .reverse()
    .find((r) => r.record_id === recordId && r.status !== "closed");
  const from = esc?.status ?? "";
  if (!esc)
    esc = append(s, "escalations", {
      record_id: recordId,
      record_code: record.code,
      stage_id: "",
      reason,
      trigger_key: `trigger.${category.toLowerCase().replace(/[^a-z]+/g, "_")}`,
      level,
      raised_by_user_id: method === "human" ? persona(s).id : null,
      raised_by_label: method === "human" ? persona(s).name : "Simulated rule evaluation",
      assigned_role: "quality_director",
      status: level === 1 ? "attention" : "open",
      due_at: level >= 2 ? due : null,
      severity: level === 3 ? "critical" : "high",
      manager_decision: "",
      decision_rationale: "",
      resulting_actions: [],
      decided_by_label: "",
      decided_at: null,
      acknowledged_at: null,
      primary_trigger_category: category,
      contributing_triggers: [],
      initiation_method: method,
      evidence_snapshot: snapshot(s, recordId),
      decision_evidence_snapshot: {},
      ai_recommendation: "",
      actions_started_at: null,
      actions_completed_at: null,
      effectiveness_result: "",
      effectiveness_verified_at: null,
      residual_risk_acceptable: null,
      closure_rationale: "",
      closed_by_label: "",
      closed_at: null,
    });
  const map = new Map<string, Row>(
    (esc.contributing_triggers ?? []).map((t: Row) => [t.category, t]),
  );
  for (const trigger of triggers) map.set(trigger.category, trigger);
  esc.contributing_triggers = [...map.values()];
  esc.level = Math.max(esc.level, level);
  if (esc.status === "attention" && esc.level >= 2) esc.status = "open";
  esc.safety_regulatory = Boolean(esc.safety_regulatory || safety);
  esc.required_participants =
    esc.level === 3
      ? [
          "Quality Director",
          "Executive sponsor",
          "Product Safety / Regulatory lead",
          "Record owner",
        ]
      : esc.level === 2
        ? ["Quality Director", "Record owner"]
        : ["Record owner"];
  esc.escalated_to_label =
    esc.level === 3
      ? "Simulated Quality Director + Executive leadership"
      : esc.level === 2
        ? "Simulated Quality Director"
        : "Simulated record owner";
  esc.decision_required ||=
    level >= 2 ? decisionRequired || `Decide the simulated response to: ${reason}` : "";
  esc.response_requirement = esc.safety_regulatory ? "immediate" : "";
  esc.response_due_at ||= esc.safety_regulatory
    ? new Date(Date.now() + 4 * 3600000).toISOString()
    : null;
  esc.financial_exposure_at_escalation = record.exposure;
  escalationEvent(
    s,
    esc,
    from ? "conditions_added" : level === 1 ? "attention_raised" : "escalated",
    from,
    reason,
    { triggers },
  );
  return esc.id;
}
function escalation(s: DemoSession, fn: string, a: Row): unknown {
  if (fn === "escalation_set_threshold") {
    requireRole(s, DIRECTORS);
    const th =
      table(s, "escalation_trigger_thresholds").find((r) => r.id === a.p_threshold_id) ??
      fail("Threshold not found.");
    const note = rationale(a.p_rationale);
    if (!Number.isFinite(a.p_new_value) || a.p_new_value < 0)
      fail("Threshold must be zero or more.");
    if (th.threshold === a.p_new_value && (a.p_active == null || th.active === a.p_active))
      fail("No change to record.");
    const eff = a.p_effective_from ?? now();
    if (!Number.isFinite(Date.parse(eff))) fail("A valid effective date is required.");
    const id = audit(s, "", "escalation.threshold_changed", "Escalation threshold changed", note, {
      oldValue: th.threshold,
      newValue: a.p_new_value,
      effectiveFrom: eff,
    });
    append(s, "escalation_threshold_changes", {
      threshold_id: th.id,
      category: th.category,
      metric: th.metric,
      level: th.level,
      old_value: th.threshold,
      new_value: a.p_new_value,
      old_active: th.active,
      new_active: a.p_active ?? th.active,
      rationale: note,
      changed_by_user_id: persona(s).id,
      changed_by_label: persona(s).name,
      approved_by_user_id: persona(s).id,
      approved_by_label: persona(s).name,
      effective_from: eff,
      audit_event_id: id,
    });
    Object.assign(th, {
      threshold: a.p_new_value,
      active: a.p_active ?? th.active,
      approval_status: "simulated_approval",
      effective_from: eff,
      updated_at: now(),
    });
    return id;
  }
  if (fn === "escalation_evaluate_record") {
    requireRole(s, CONTRIBUTORS);
    const pr = findRecord(s, a.p_record_id);
    const r = pr.record as ProblemRecord;
    const signals = table(s, "signals").filter((x) => x.linked_record_id === pr.id);
    const max = (key: string) =>
      signals.length ? Math.max(...signals.map((x) => Number(x[key]) || 0)) : 0;
    const metrics: Row = {
      exposure: Number(pr.exposure),
      claims: r.claims,
      units_in_service: r.unitsInService,
      signal_severity: max("current_severity"),
      recurrence_count: max("recurrence_count"),
      customer_impact: max("customer_impact"),
      rising_signals: signals.filter((x) => x.trend_direction === "rising").length,
      overdue_stages: r.stages.filter(
        (x) => x.status !== "complete" && Date.parse(x.dueDate) < Date.now(),
      ).length,
      violated_clauses: r.clauses.filter((x) => x.verdict === "Violated").length,
      safety_regulatory: signals.some((x) => x.safety_relevant || x.compliance_relevant),
    };
    const best = new Map<string, Row>();
    for (const th of [...table(s, "escalation_trigger_thresholds")].sort(
      (a, b) => a.level - b.level,
    ))
      if (
        th.active &&
        Date.parse(th.effective_from) <= Date.now() &&
        metrics[th.metric] != null &&
        Number(metrics[th.metric]) >= Number(th.threshold)
      )
        best.set(th.category, {
          category: th.category,
          source: "rule",
          rule: `${th.metric} ≥ ${th.threshold} ${th.unit} (Level ${th.level})`,
          threshold: th.threshold,
          thresholdId: th.id,
          observed: metrics[th.metric],
          level: th.level,
          at: now(),
        });
    if (metrics.safety_regulatory)
      best.set("Safety / Regulatory", {
        category: "Safety / Regulatory",
        source: "rule",
        rule: "Safety / regulatory override",
        threshold: "",
        observed: "Linked safety/compliance signal",
        level: 3,
        at: now(),
      });
    const met = [...best.values()].sort((a, b) => b.level - a.level);
    if (!met.length) return { met, metrics, escalationId: null };
    const primary = metrics.safety_regulatory ? "Safety / Regulatory" : met[0].category;
    for (const t of met) t.primary = t.category === primary;
    return {
      met,
      metrics,
      level: met[0].level,
      escalationId: escalationMerge(
        s,
        pr.id,
        met[0].level,
        primary,
        met,
        "rule",
        metrics.safety_regulatory,
        `Approved synthetic condition met: ${primary}`,
      ),
    };
  }
  if (fn === "escalation_raise_human") {
    requireRole(s, CONTRIBUTORS);
    if (![1, 2, 3].includes(a.p_level)) fail("Level must be 1, 2 or 3.");
    const note = rationale(a.p_rationale);
    const category = a.p_category || "Human Initiated";
    return escalationMerge(
      s,
      a.p_record_id,
      a.p_level,
      category,
      [
        {
          category,
          primary: true,
          source: "human",
          rule: `Simulated initiation by ${persona(s).name}`,
          threshold: "",
          observed: note,
          level: a.p_level,
          at: now(),
        },
      ],
      "human",
      Boolean(a.p_safety),
      note,
      a.p_decision_required,
    );
  }
  const esc =
    table(s, "escalations").find((r) => r.id === a.p_escalation_id) ??
    fail("Escalation not found.");
  const from = esc.status;
  const actor = persona(s);
  const canDecide = () =>
    requireRole(s, [...DIRECTORS, String(esc.assigned_role ?? "quality_director")]);
  if (fn === "escalation_acknowledge") {
    canDecide();
    if (esc.status !== "open") fail("Only a new escalation can be acknowledged.");
    esc.status = "awaiting_decision";
    esc.acknowledged_at = now();
    return escalationEvent(s, esc, "acknowledged", from, String(a.p_comment ?? ""));
  }
  if (fn === "escalation_record_decision") {
    canDecide();
    if (!["open", "awaiting_decision"].includes(esc.status))
      fail("This escalation has already been decided.");
    if (esc.raised_by_user_id === actor.id)
      fail("The person who raised an escalation cannot decide it.");
    if (
      !["Approve", "Approve with conditions", "Reject", "Defer", "Redirect"].includes(a.p_decision)
    )
      fail("Unknown management decision.");
    const note = rationale(a.p_rationale);
    if (!Array.isArray(a.p_actions) || a.p_actions.some((x: unknown) => typeof x !== "string"))
      fail("Resulting actions must be a list of text.");
    Object.assign(esc, {
      status: "decided",
      manager_decision: a.p_decision,
      decision_rationale: note,
      resulting_actions: a.p_actions,
      decision_evidence_snapshot: snapshot(s, esc.record_id),
      decided_by_user_id: actor.id,
      decided_by_label: actor.name,
      decided_at: now(),
      acknowledged_at: esc.acknowledged_at ?? now(),
      resolved_at: now(),
      resolution: a.p_decision,
    });
    return escalationEvent(s, esc, "decided", from, note, { actions: a.p_actions });
  }
  if (fn === "escalation_change_level") {
    if (from === "closed") fail("A closed escalation cannot change level.");
    if (![1, 2, 3].includes(a.p_new_level) || esc.level === a.p_new_level)
      fail("Choose a different level (1–3).");
    const note = rationale(a.p_rationale);
    const down = a.p_new_level < esc.level;
    requireRole(s, down ? DIRECTORS : CONTRIBUTORS);
    if (
      down &&
      (!Array.isArray(a.p_evidence) ||
        !a.p_evidence.some((x: unknown) => typeof x === "string" && x.trim()))
    )
      fail("De-escalation requires supporting evidence.");
    if (
      down &&
      a.p_new_level === 1 &&
      ["decided", "actions_in_progress", "effectiveness_verification"].includes(from)
    )
      fail("A decided escalation cannot return to Attention.");
    const old = esc.level;
    esc.level = a.p_new_level;
    if (esc.level === 1 && ["open", "awaiting_decision"].includes(from)) esc.status = "attention";
    else if (esc.level >= 2 && from === "attention") esc.status = "open";
    esc.escalated_to_label =
      esc.level === 3
        ? "Simulated Quality Director + Executive leadership"
        : esc.level === 2
          ? "Simulated Quality Director"
          : "Simulated record owner";
    esc.required_participants =
      esc.level === 3
        ? [
            "Quality Director",
            "Executive sponsor",
            "Product Safety / Regulatory lead",
            "Record owner",
          ]
        : esc.level === 2
          ? ["Quality Director", "Record owner"]
          : ["Record owner"];
    return escalationEvent(s, esc, down ? "de-escalated" : "escalated", from, note, {
      fromLevel: old,
      toLevel: esc.level,
      evidence: a.p_evidence,
    });
  }
  if (fn === "escalation_advance_lifecycle") {
    requireRole(s, EDITORS);
    const note = rationale(a.p_note);
    const allowed =
      (from === "decided" && a.p_to === "actions_in_progress") ||
      (from === "actions_in_progress" && a.p_to === "effectiveness_verification") ||
      (from === "effectiveness_verification" && ["actions_in_progress", "closed"].includes(a.p_to));
    if (!allowed) fail("That lifecycle step is not allowed from the current status.");
    if (a.p_to === "closed") {
      requireRole(s, DIRECTORS);
      if (
        !esc.manager_decision ||
        !esc.actions_completed_at ||
        a.p_effective !== true ||
        a.p_residual_ok !== true ||
        !Array.isArray(a.p_evidence) ||
        !a.p_evidence.some((x: unknown) => typeof x === "string" && x.trim())
      )
        fail(
          "Closure requires a decision, completed actions, verified effectiveness, acceptable residual risk and evidence.",
        );
      Object.assign(esc, {
        effectiveness_result: "verified",
        effectiveness_verified_at: now(),
        residual_risk_acceptable: true,
        closure_rationale: note,
        closed_by_user_id: actor.id,
        closed_by_label: actor.name,
        closed_at: now(),
      });
    }
    if (a.p_to === "actions_in_progress") esc.actions_started_at ??= now();
    if (a.p_to === "effectiveness_verification") esc.actions_completed_at = now();
    if (from === "effectiveness_verification" && a.p_to === "actions_in_progress") {
      esc.effectiveness_result = "not effective";
      esc.actions_completed_at = null;
    }
    esc.status = a.p_to;
    return escalationEvent(s, esc, a.p_to === "closed" ? "closed" : a.p_to, from, note, {
      evidence: a.p_evidence,
      effective: a.p_effective,
      residualRiskAcceptable: a.p_residual_ok,
    });
  }
  fail(`Unsupported escalation command: ${fn}`);
}

export function command(s: DemoSession, fn: string, a: Row): unknown {
  if (fn === "demo_request_closure") {
    requireRole(s, EDITORS);
    const record = findRecord(s, a.p_record_id).record as ProblemRecord;
    const last = record.stages.at(-1) ?? fail("Workflow stages are unavailable.");
    if (
      record.status === "closed" ||
      record.stages.some((stage) => stage.index < last.index && stage.status !== "complete")
    )
      fail("Complete earlier workflow stages before requesting closure review.");
    const gate = stageGate(
      record,
      last,
      normalizeMapping(policy(s, "workflow.eightd_mapping", null)),
    );
    if (gate.blocked) fail(gate.message ?? "Final evidence gate is incomplete.");
    return audit(
      s,
      record.id,
      "demo.closure_requested",
      "Closure review requested",
      rationale(a.p_rationale),
      { fingerprint: closureFingerprint(record) },
      "record.closure",
    );
  }
  if (["pfmea_propose_change", "pfmea_sign_off"].includes(fn)) return pfmea(s, fn, a);
  if (
    [
      "escalation_acknowledge",
      "escalation_record_decision",
      "escalation_evaluate_record",
      "escalation_raise_human",
      "escalation_change_level",
      "escalation_advance_lifecycle",
      "escalation_set_threshold",
    ].includes(fn)
  )
    return escalation(s, fn, a);
  if (fn === "compliance_set_verdict") {
    const row = findRecord(s, a.p_record_id);
    const r = row.record as ProblemRecord;
    const clause = r.clauses.find((x) => x.id === a.p_clause_id);
    const invalid = validateVerdictChange(clause, a.p_new_verdict, String(a.p_rationale ?? ""));
    if (invalid || !clause) fail(invalid ?? "Clause not found.");
    if (!canChangeVerdict(persona(s).roles, clause.verdict, a.p_new_verdict))
      fail("This simulated persona cannot change this compliance verdict.");
    const actor = persona(s);
    const rule = verdictRuleKey(clause.verdict, a.p_new_verdict);
    const note = rationale(a.p_rationale);
    const auditId = audit(
      s,
      row.id,
      "compliance.verdict_changed",
      "Compliance verdict changed",
      note,
      {
        clauseId: clause.id,
        oldVerdict: clause.verdict,
        newVerdict: a.p_new_verdict,
        evidence: a.p_evidence,
      },
      rule,
    );
    const change = append(s, "compliance_verdict_changes", {
      record_id: row.id,
      record_code: row.code,
      clause_id: clause.id,
      standard: clause.standard,
      clause: clause.clause,
      old_verdict: clause.verdict,
      new_verdict: a.p_new_verdict,
      rationale: note,
      evidence: a.p_evidence ?? {},
      assessed_by_user_id: actor.id,
      assessed_by_label: actor.name,
      assessed_by_title: actor.jobTitle,
      authority_rule_key: rule,
      audit_event_id: auditId,
    });
    Object.assign(clause, {
      verdict: a.p_new_verdict,
      rationale: note,
      assessedBy: actor.name,
      assessedByTitle: actor.jobTitle,
      assessedAt: now(),
      authorityRuleKey: rule,
      lastChangeId: change.id,
    });
    return change.id;
  }
  if (["financial_set_record_carry_factor", "financial_set_default_carry_factor"].includes(fn)) {
    requireRole(s, DIRECTORS);
    const invalid = validateCarryFactor(a.p_new_value, String(a.p_rationale ?? ""));
    if (invalid) fail(invalid);
    const actor = persona(s);
    const isDefault = fn === "financial_set_default_carry_factor";
    const row = isDefault
      ? (table(s, "policy_settings").find(
          (r) => r.key === "financial.delay_carry_factor_default",
        ) ?? fail("Default carry factor policy not found."))
      : findRecord(s, a.p_record_id);
    const old = isDefault ? Number(row.value) : row.record.financial.monthlyCarryFactor;
    if (old === a.p_new_value) fail("The new carry factor matches the current value.");
    const id = audit(
      s,
      isDefault ? "" : row.id,
      isDefault ? "financial.carry_factor_default_changed" : "financial.carry_factor_changed",
      "Financial carry factor changed",
      rationale(a.p_rationale),
      { oldValue: old, newValue: a.p_new_value },
    );
    // The source records carry-factor history in audit_events; no extra schema is invented.
    if (isDefault) row.value = a.p_new_value;
    else
      Object.assign(row.record.financial, {
        monthlyCarryFactor: a.p_new_value,
        monthlyCarryFactorSource: "per-record override",
        monthlyCarryFactorUpdatedBy: actor.name,
        monthlyCarryFactorUpdatedAt: now(),
      });
    row.updated_at = now();
    return id;
  }
  if (fn === "manage_user_role") {
    requireRole(s, DIRECTORS);
    const note = rationale(a.p_rationale);
    const actor = persona(s);
    if (a.p_user_id === actor.id) fail("You cannot change your own simulated roles.");
    if (!table(s, "profiles").some((r) => r.id === a.p_user_id)) fail("Simulated user not found.");
    if (
      ![
        "admin",
        "quality_director",
        "quality_engineer",
        "manufacturing_engineer",
        "supplier_quality",
        "viewer",
      ].includes(a.p_role) ||
      typeof a.p_grant !== "boolean"
    )
      fail("Unknown simulated role operation.");
    const roles = table(s, "user_roles");
    const at = roles.findIndex((r) => r.user_id === a.p_user_id && r.role === a.p_role);
    if ((a.p_grant && at >= 0) || (!a.p_grant && at < 0))
      fail("Role assignment already has that state.");
    if (a.p_grant)
      roles.push({ id: uid(), user_id: a.p_user_id, role: a.p_role, granted_by: actor.id });
    else {
      const remaining = roles.filter((_, i) => i !== at);
      if (!remaining.some((r) => DIRECTORS.includes(r.role)))
        fail("Cannot remove the last privileged simulated user.");
      roles.splice(at, 1);
    }
    return audit(s, "", "user_role.changed", "Simulated role assignment changed", note, {
      target_user_id: a.p_user_id,
      role: a.p_role,
      operation: a.p_grant ? "grant" : "remove",
    });
  }
  // Server-only reset/import/internal helper functions are intentionally not callable.
  fail(`Unsupported demo RPC: ${fn}`);
}
